import { ReactNode, useState } from 'react';
import { LuFilter } from 'react-icons/lu';

import { Button } from '@/components/ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Label } from '@/components/ui/label';

export interface MultiFilterSection<TId extends string> {
    id: TId;
    label: string;
    count: number;
    content: ReactNode;
    onClear?: () => void;
}

interface MultiFilterProps<TId extends string> {
    sections: MultiFilterSection<TId>[];
    defaultSectionId: TId;
    contentClassName?: string;
    dropdownClassName?: string;
    onOpenChange?: (open: boolean) => void;
    onSectionChange?: (sectionId: TId) => void;
}

export const MultiFilter = <TId extends string>({
    sections,
    defaultSectionId,
    contentClassName = 'min-h-0 flex-1 overflow-y-auto p-4 pt-3',
    dropdownClassName = 'w-96 p-0',
    onOpenChange,
    onSectionChange,
}: MultiFilterProps<TId>) => {
    const [isFilterOpen, setIsFilterOpen] = useState(false);
    const [activeSectionId, setActiveSectionId] = useState<TId>(defaultSectionId);
    const activeSection = sections.find((section) => section.id === activeSectionId) ?? sections[0];
    const totalFilterCount = sections.reduce((total, section) => total + section.count, 0);
    const filterButtonText =
        totalFilterCount === 0 ? 'Filter' : `${totalFilterCount} filter${totalFilterCount > 1 ? 's' : ''}`;

    const handleOpenChange = (open: boolean) => {
        setIsFilterOpen(open);
        onOpenChange?.(open);
    };

    const handleSectionChange = (sectionId: TId) => {
        setActiveSectionId(sectionId);
        onSectionChange?.(sectionId);
    };

    return (
        <DropdownMenu open={isFilterOpen} onOpenChange={handleOpenChange}>
            <DropdownMenuTrigger asChild>
                <Button variant="outline2" size="sm" className="flex items-center space-x-1">
                    <LuFilter className="mr-1 h-4 w-4" />
                    <span>{filterButtonText}</span>
                    {totalFilterCount > 0 && (
                        <div className="ml-1 flex h-5 w-5 items-center justify-center rounded-full bg-blue-500 text-xs text-white">
                            {totalFilterCount}
                        </div>
                    )}
                </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent className={dropdownClassName} align="start">
                <div className="flex h-full">
                    <div className="w-36 border-r bg-gray-50">
                        <div className="p-3">
                            <Label className="text-xs font-medium tracking-wide text-gray-600 uppercase">Filters</Label>
                        </div>
                        <div className="space-y-1">
                            {sections.map((section) => {
                                const isActive = section.id === activeSection?.id;

                                return (
                                    <button
                                        key={section.id}
                                        onClick={() => handleSectionChange(section.id)}
                                        className={`flex w-full items-center justify-between px-3 py-2 text-left text-sm hover:bg-gray-100 ${
                                            isActive
                                                ? 'border-r-2 border-blue-500 bg-blue-50 text-blue-700'
                                                : 'text-gray-700'
                                        }`}
                                    >
                                        <span>{section.label}</span>
                                        {section.count > 0 && (
                                            <div className="flex h-4 w-4 items-center justify-center rounded-full bg-blue-500 text-xs text-white">
                                                {section.count}
                                            </div>
                                        )}
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                    <div className="flex min-h-0 flex-1 flex-col">
                        <div className="flex items-center justify-between border-b p-4 pt-2">
                            <Label className="text-sm font-medium">{activeSection?.label}</Label>
                            {activeSection && activeSection.count > 0 && activeSection.onClear && (
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={activeSection.onClear}
                                    className="h-auto p-1 text-xs"
                                >
                                    Clear
                                </Button>
                            )}
                        </div>
                        <div className={contentClassName}>{activeSection?.content}</div>
                    </div>
                </div>
            </DropdownMenuContent>
        </DropdownMenu>
    );
};
